import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Divider,
  Alert,
} from "@mui/material";
import Div from "@jumbo/shared/Div";
import { Formik } from "formik";
import * as Yup from "yup";
import { makeFormikYupValidator } from "app/utils/validation";

const DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const buildVisibilityInitial = (visibility) => {
  const normalizeTime = (val) => {
    if (typeof val !== "string") return "";
    const m = val.match(/^(\d{1,2}):(\d{2})/);
    if (!m) return "";
    const hh = String(Math.max(0, Math.min(23, Number(m[1])))).padStart(2, "0");
    const mm = String(Math.max(0, Math.min(59, Number(m[2])))).padStart(2, "0");
    return `${hh}:${mm}`;
  };
  const out = {};
  DAYS.forEach((day) => {
    const src = visibility && visibility[day] ? visibility[day] : {};
    const intervals = Array.isArray(src.intervals)
      ? src.intervals
          .map((it) => ({
            from: normalizeTime(it?.from),
            to: normalizeTime(it?.to),
          }))
          .filter((it) => it.from && it.to)
      : [];
    out[day] = {
      enabled: Boolean(src.enabled),
      intervals,
    };
  });
  return out;
};

const timeStringToMinutes = (t) => {
  if (typeof t !== "string") return NaN;
  const m = t.match(/^(\d{2}):(\d{2})$/);
  if (!m) return NaN;
  return Number(m[1]) * 60 + Number(m[2]);
};

// Snap "HH:mm" to nearest 5-minute step.
// - Rounds to nearest 5 minutes (half-up).
// - Carries hour if needed.
// - Clamps to 23:55 (since only 5-min steps allowed).
const snapTo5Minutes = (time) => {
  if (typeof time !== "string") return "";
  const m = time.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return time;

  let hh = Number(m[1]);
  let mm = Number(m[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return time;

  hh = Math.max(0, Math.min(23, hh));
  mm = Math.max(0, Math.min(59, mm));

  const total = hh * 60 + mm;
  let rounded = Math.round(total / 5) * 5;

  // Clamp to last valid 5-min slot
  const max = 23 * 60 + 55;
  if (rounded > max) rounded = max;
  if (rounded < 0) rounded = 0;

  const outH = String(Math.floor(rounded / 60)).padStart(2, "0");
  const outM = String(rounded % 60).padStart(2, "0");
  return `${outH}:${outM}`;
};

const CategoryVisibility = ({ category, onEditCategory, loading }) => {
  const { t } = useTranslation();

  const initialValues = {
    visibility: buildVisibilityInitial(category?.visibility),
  };

  const schema = Yup.object().shape({
    visibility: Yup.object(
      DAYS.reduce((acc, day) => {
        acc[day] = Yup.object({
          enabled: Yup.boolean().required(),
          intervals: Yup.array()
            .of(
              Yup.object({
                from: Yup.string()
                  .required()
                  .matches(/^\d{2}:\d{2}$/),
                to: Yup.string()
                  .required()
                  .matches(/^\d{2}:\d{2}$/),
              })
            )
            .test(
              "no-overlap-and-order",
              t("validation.requiredField", {
                defaultValue: "Intervals are invalid or overlapping",
              }),
              function (list) {
                const parent = this?.from?.[0] || this?.parent || {};
                const enabled = Boolean(parent.enabled);
                if (!enabled) return true;
                const intervals = Array.isArray(list) ? list : [];
                const mins = intervals
                  .map((it) => ({
                    from: timeStringToMinutes(it?.from),
                    to: timeStringToMinutes(it?.to),
                  }))
                  .filter(
                    (it) => !Number.isNaN(it.from) && !Number.isNaN(it.to)
                  );
                if (mins.length !== intervals.length) return false;
                for (let i = 0; i < mins.length; i++) {
                  if (!(mins[i].from < mins[i].to)) return false;
                }
                const sorted = mins.slice().sort((a, b) => a.from - b.from);
                for (let i = 1; i < sorted.length; i++) {
                  if (sorted[i].from < sorted[i - 1].to) return false;
                }
                return true;
              }
            ),
        });
        return acc;
      }, {})
    ),
  });

  const validate = makeFormikYupValidator(schema, (missing) => {
    alert(
      `${t("messages.checkRequiredFields")}\n` +
        missing.map((p) => `• ${p}`).join("\n")
    );
  });
  return (
    <Box sx={{ p: 3 }}>
      <Formik
        validateOnChange={true}
        validateOnSubmit={true}
        enableReinitialize={true}
        initialValues={initialValues}
        validate={validate}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const payload = { ...category, visibility: values.visibility };
            await onEditCategory(payload);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ values, errors, setFieldValue, handleSubmit, isSubmitting }) => (
          <form
            noValidate
            onSubmit={(e) => {
              handleSubmit(e);
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 2 }}
            >
              <Alert variant="outlined" severity="info">
                {t("pages.dsokCategories.visibilityHelpText")}
              </Alert>
            </Typography>
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                p: 1.5,
                mb: 3,
              }}
            >
              {DAYS.map((day) => {
                const dayData = values.visibility?.[day] || {
                  enabled: false,
                  intervals: [],
                };
                const dayLabel = t(`days.${day}`) || t(`days.${day}`) || day;
                const dayError =
                  errors?.visibility &&
                  errors.visibility[day] &&
                  errors.visibility[day].intervals;
                return (
                  <Box
                    key={day}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      mb: 1.5,
                      p: 1,
                      borderRadius: 1,
                      bgcolor: dayData.enabled ? "action.hover" : "inherit",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">{dayLabel}</Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(dayData.enabled)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setFieldValue(
                                `visibility.${day}.enabled`,
                                checked
                              );
                              if (
                                checked &&
                                !Array.isArray(dayData.intervals)
                              ) {
                                setFieldValue(
                                  `visibility.${day}.intervals`,
                                  []
                                );
                              }
                            }}
                            name={`visibility.${day}.enabled`}
                            color="primary"
                          />
                        }
                        label={t("common.active", {
                          defaultValue: "Active",
                        })}
                        sx={{ m: 0 }}
                      />
                    </Box>
                    {dayData.enabled && (
                      <Box sx={{ pl: 0.5 }}>
                        {(dayData.intervals || []).map((interval, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <TextField
                              size="small"
                              type="time"
                              label={t("pages.dsokCategories.timeFrom")}
                              value={interval?.from || ""}
                              onChange={(e) =>
                                setFieldValue(
                                  `visibility.${day}.intervals.${idx}.from`,
                                  e.target.value
                                )
                              }
                              onBlur={(e) => {
                                const snapped = snapTo5Minutes(e.target.value);
                                if (snapped && snapped !== e.target.value) {
                                  setFieldValue(
                                    `visibility.${day}.intervals.${idx}.from`,
                                    snapped
                                  );
                                }
                              }}
                              InputLabelProps={{ shrink: true }}
                              inputProps={{ step: 300 }}
                            />
                            <TextField
                              size="small"
                              type="time"
                              label={t("pages.dsokCategories.timeTo")}
                              value={interval?.to || ""}
                              onChange={(e) =>
                                setFieldValue(
                                  `visibility.${day}.intervals.${idx}.to`,
                                  e.target.value
                                )
                              }
                              onBlur={(e) => {
                                const snapped = snapTo5Minutes(e.target.value);
                                if (snapped && snapped !== e.target.value) {
                                  setFieldValue(
                                    `visibility.${day}.intervals.${idx}.to`,
                                    snapped
                                  );
                                }
                              }}
                              InputLabelProps={{ shrink: true }}
                              inputProps={{ step: 300 }}
                            />
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => {
                                const next = (dayData.intervals || []).filter(
                                  (_, i) => i !== idx
                                );
                                setFieldValue(
                                  `visibility.${day}.intervals`,
                                  next
                                );
                              }}
                            >
                              ×
                            </IconButton>
                          </Box>
                        ))}
                        {(!dayData.intervals ||
                          dayData.intervals.length === 0) && (
                          <Typography variant="caption" color="text.secondary">
                            {t("pages.dsokCategories.noIntervals", {
                              defaultValue: "No intervals added",
                            })}
                          </Typography>
                        )}
                        <Box sx={{ mt: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              const next = Array.isArray(dayData.intervals)
                                ? [...dayData.intervals]
                                : [];
                              next.push({ from: "08:00", to: "17:00" });
                              setFieldValue(
                                `visibility.${day}.intervals`,
                                next
                              );
                            }}
                          >
                            {t("pages.dsokCategories.addInterval", {
                              defaultValue: "Add interval",
                            })}
                          </Button>
                        </Box>
                        {dayError && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ mt: 0.5, display: "block" }}
                          >
                            {t("pages.dsokCategories.intervalError", {
                              defaultValue:
                                "Intervals are invalid or overlapping",
                            })}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Div sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || loading}
                size="large"
              >
                {t("buttons.save")}
              </Button>
            </Div>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default CategoryVisibility;
